import { ApolloError } from '@apollo/client';
import { Info } from 'react-feather';
import { useEffect, useRef, useState } from 'react';

import {
  GetExternalValidationResultQuery as GetExternalValidationResult,
  ParticipantFieldType,
} from '__generated__/graphql';

import { getServerErrors } from '../helpers';
import AddressInput from './Types/AddressInput';
import AuLicenseInput from './Types/AuLicenseInput';
import IntegerInput from './Types/IntegerInput';
import OptionInput from './Types/OptionInput';
import ParticipantFieldInputProps from './ParticipantFieldInputProps';
import SelectionInput from './Types/SelectionInput';
import ShortTextInput from './Types/ShortTextInput';
import TimeInput from './Types/TimeInput';
import UI from '../UI';
import useExternalValidation from './Types/useExternalValidation';

const ParticipantFieldInput = ({
  value, onChange, field, readyForExternalValidation, registration, updateCustomErrors, updateValidating, ...props
}: ParticipantFieldInputProps) => {
  const [focused, setFocused] = useState(false);
  const [key, setKey] = useState(1);
  const [result, setResult] = useState<GetExternalValidationResult['participantFieldValidation'] | null>(null);

  const validationEnabled = readyForExternalValidation && field.has_external_validation && !!registration;

  const hasValue = !!value.value || value.choices?.length > 0;

  const { validating } = useExternalValidation({
    field,
    input: value,
    enabled: validationEnabled && !focused, // Run validation directly on blur
    registration,
    onSuccess: ({ participantFieldValidation }) => {
      if (!focused && !hasValue && participantFieldValidation.suggestion) {
        // Autocomplete the input if it is not focused and does not have a value
        onChange({
          ...value,
          value: participantFieldValidation.suggestion,
        });

        // Remount the input to show the new value
        setKey((key) => key + 1);
      }

      setResult(participantFieldValidation);
      updateCustomErrors?.({});
      updateValidating?.([]);
    },
    onError: (error: ApolloError) => {
      setResult(null);
      // Only set errors if the input has a value, otherwise the user
      // might get blocked when the input is actually optional
      updateCustomErrors?.(hasValue ? getServerErrors(error, undefined, false) : {});
      updateValidating?.([]);

      // Update touched state to ensure errors are displayed
      props.onBlur?.();
    },
  });

  const validationEnabledRef = useRef<boolean>();

  useEffect(() => {
    if (validationEnabled !== validationEnabledRef.current) {
      if (validationEnabled) {
        updateValidating?.(['external_validation']);
      } else {
        setResult(null);
        updateCustomErrors?.({});
        updateValidating?.([]);
      }

      validationEnabledRef.current = validationEnabled;
    }
  }, [validationEnabled, updateValidating, updateCustomErrors]);

  const handleFocus = () => {
    setFocused(true);
    props.onFocus?.();
  };

  const handleBlur = () => {
    setFocused(false);
    props.onBlur?.();
  };

  const inputs = {
    [ParticipantFieldType.short_text]: ShortTextInput,
    [ParticipantFieldType.integer]: IntegerInput,
    [ParticipantFieldType.option]: OptionInput,
    [ParticipantFieldType.selection]: SelectionInput,
    [ParticipantFieldType.iban]: ShortTextInput,
    [ParticipantFieldType.time]: TimeInput,
    [ParticipantFieldType.address]: AddressInput,
    [ParticipantFieldType.au_license]: AuLicenseInput,
    [ParticipantFieldType.knwu_license]: ShortTextInput,
    [ParticipantFieldType.kwbn_member]: ShortTextInput,
    [ParticipantFieldType.ntfu_member]: ShortTextInput,
  };

  const Input = inputs[field.type];

  return (
    <>
      <Input
        {...props}
        field={field}
        onChange={onChange}
        value={value}
        registration={registration}
        onFocus={handleFocus}
        onBlur={handleBlur}
        validating={validating}
        key={key}
      />
      {result?.success && result?.message && (
        <UI.Info icon={<Info />}>
          {result.message}
        </UI.Info>
      )}
    </>
  );
};

export default ParticipantFieldInput;
